import { useEffect, useMemo, useRef, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Input, Modal, Select, useToast } from '../../../design-system';
import { money, ageLabel } from '../../pages/OrgAdminFinance';
import '../../styles/Finance.css';

// Raise a draft invoice.
//
// The tax preview here MIRRORS the server's rule (fees taxed, OPE exempt, split
// CGST/SGST unless inter-state). It is a preview only — the server recomputes
// everything on save from the client's own place of supply, which is the one
// that ends up on the document.

const KINDS = [
  { value: 'professional_fees', label: 'Professional fees (taxable)' },
  { value: 'ope', label: 'Out-of-pocket / reimbursement (no GST)' },
];

const blankLine = () => ({ description: '', kind: 'professional_fees', amount: '' });

// A task charge becomes an invoice line, carrying its provenance so the charge is
// marked billed and the line can point back at the task.
const lineFromCharge = (c) => ({
  description: c.description || c.title,
  kind: c.kind === 'ope' ? 'ope' : 'professional_fees',
  amount: String(c.amount),
  sourceType: 'task_bill', sourceId: c.billItemId, taskId: c.taskId,
});

function InvoiceBuilder({ preset = {}, onClose, onCreated }) {
  const toast = useToast();
  const [clients, setClients] = useState([]);
  const [settings, setSettings] = useState(null);
  const [clientId, setClientId] = useState(preset.clientId || '');
  const [lines, setLines] = useState(
    (preset.charges || []).length ? preset.charges.map(lineFromCharge) : [blankLine()]
  );
  // The client's uninvoiced work, minus whatever is already on the invoice.
  // Without this, choosing a client told you nothing and the only way to bill
  // real work was to total it up elsewhere and retype the figure.
  const [available, setAvailable] = useState([]);
  const [loadingWork, setLoadingWork] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState('');
  const [gstApplicable, setGstApplicable] = useState(true);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      apiClient.get('/api/clients/options').catch(() => ({ data: [] })),
      apiClient.get('/api/finance/settings').catch(() => ({ data: { settings: {} } })),
    ]).then(([c, s]) => {
      const list = Array.isArray(c.data) ? c.data : (c.data?.clients || []);
      setClients(list);
      setSettings(s.data.settings || {});
      if (s.data.settings?.paymentTermDays != null && !dueDate) {
        const d = new Date();
        d.setDate(d.getDate() + s.data.settings.paymentTermDays);
        setDueDate(d.toISOString().slice(0, 10));
      }
      // `gstApplicableByDefault` is a setting the firm sets and this dialog was
      // ignoring — the exact trap of a field that reads back fine and changes
      // nothing. Only applied when the caller didn't pre-seed the invoice.
      if (s.data.settings?.gstApplicableByDefault === false) setGstApplicable(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Whether the user has actually typed anything, so auto-filling from the
  // client's unbilled work can never overwrite their own work in progress.
  const linesAreUntouched = (ls) =>
    ls.every((l) => !String(l.description || '').trim() && !Number(l.amount) && !l.sourceId);

  // The current lines, readable synchronously. The fetch below has to branch on
  // what the user has already typed, and a setState updater must stay pure — so
  // it reads this rather than deciding inside setLines and calling setAvailable
  // from in there (which double-fires under StrictMode).
  const linesRef = useRef(lines);
  useEffect(() => { linesRef.current = lines; }, [lines]);

  // Pull the client's uninvoiced charges the moment a client is chosen. Charges
  // already on the invoice are filtered out, so deleting a line puts it back on
  // offer rather than losing it.
  useEffect(() => {
    if (!clientId) { setAvailable([]); return; }
    let cancelled = false;
    setLoadingWork(true);
    apiClient.get('/api/finance/unbilled', { params: { clientId } })
      .then((r) => {
        if (cancelled) return;
        const rows = (r.data?.charges || []).filter((c) => c.clientId === clientId);
        const current = linesRef.current;
        const already = new Set(current.map((l) => l.sourceId).filter(Boolean));
        const fresh = rows.filter((c) => !already.has(c.billItemId));
        // Nothing typed yet ⇒ fill the invoice from the work. This is the
        // zero-click case and the reason the dialog exists.
        if (fresh.length && linesAreUntouched(current)) {
          setLines(fresh.map(lineFromCharge));
          setAvailable([]);
        } else {
          setAvailable(fresh);
        }
      })
      // A failure here must not block a hand-typed invoice.
      .catch(() => !cancelled && setAvailable([]))
      .finally(() => !cancelled && setLoadingWork(false));
    return () => { cancelled = true; };
  }, [clientId]);

  const setLine = (i, field, v) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, [field]: v } : l)));

  // Move charges onto the invoice, dropping any blank starter line.
  const addCharges = (rows) => {
    if (!rows.length) return;
    setLines((prev) => [...prev.filter((l) => !linesAreUntouched([l])), ...rows.map(lineFromCharge)]);
    const taken = new Set(rows.map((c) => c.billItemId));
    setAvailable((prev) => prev.filter((c) => !taken.has(c.billItemId)));
  };

  // A line removed by hand goes back on offer, so a charge can't be lost by
  // accident — the same reasoning as filtering `available` by what is on the
  // invoice rather than deleting from it.
  const removeLine = (i) => {
    const gone = linesRef.current[i];
    if (gone?.sourceType === 'task_bill' && gone.sourceId) {
      setAvailable((av) => (av.some((c) => c.billItemId === gone.sourceId) ? av : [...av, {
        billItemId: gone.sourceId, taskId: gone.taskId, description: gone.description,
        kind: gone.kind, amount: Number(gone.amount) || 0, taskNumber: null, ageDays: null,
      }]));
    }
    setLines((prev) => {
      const next = prev.filter((_, idx) => idx !== i);
      return next.length ? next : [blankLine()];
    });
  };

  const availableTotal = available.reduce((s, c) => s + (c.amount || 0), 0);
  const pulled = lines.filter((l) => l.sourceType === 'task_bill' && l.sourceId);

  const preview = useMemo(() => {
    const num = (v) => Number(v) || 0;
    const taxable = lines.filter((l) => l.kind !== 'ope').reduce((s, l) => s + num(l.amount), 0);
    const exempt = lines.filter((l) => l.kind === 'ope').reduce((s, l) => s + num(l.amount), 0);
    const rate = gstApplicable ? Number(settings?.gstRate || 0) : 0;
    const tax = (taxable * rate) / 100;
    const tdsPct = settings?.tdsAppliesByDefault ? Number(settings?.tdsPercent || 0) : 0;
    const tds = (taxable * tdsPct) / 100;
    return {
      taxable, exempt, tax, tdsPct, tds,
      total: taxable + exempt + tax,
      expected: taxable + exempt + tax - tds,
    };
  }, [lines, gstApplicable, settings]);

  const save = async () => {
    if (!clientId) { toast.danger('Choose a client'); return; }
    const payload = lines
      .filter((l) => String(l.description).trim() && Number(l.amount) > 0)
      .map((l) => ({
        description: l.description.trim(),
        kind: l.kind,
        amount: Number(l.amount),
        sourceType: l.sourceType || 'manual',
        sourceId: l.sourceId || null,
        taskId: l.taskId || null,
      }));
    if (payload.length === 0) { toast.danger('Add at least one line with a description and amount'); return; }

    setSaving(true);
    try {
      const r = await apiClient.post('/api/finance/invoices', {
        clientId, lineItems: payload, invoiceDate, dueDate: dueDate || undefined,
        gstApplicable, notes,
      });
      toast.success('Draft invoice created');
      onCreated?.(r.data.invoice.id);
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not create the invoice');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen onClose={onClose} title="New invoice" size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={save} loading={saving}>Create draft</Button>
        </>
      }
    >
      <div className="fin-form">
        <div className="fin-form-row">
          <Select
            label="Client" value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            disabled={!!preset.clientId}
          >
            <option value="">— choose —</option>
            {clients.map((c) => (
              <option key={c._id || c.id} value={c._id || c.id}>
                {c.displayName || c.name}{c.clientCode ? ` (${c.clientCode})` : ''}
              </option>
            ))}
          </Select>
          <Input label="Invoice date" type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
          <Input label="Due date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </div>

        {/* The client's uninvoiced work, offered rather than left for someone to
            total up by hand elsewhere. Oldest first (FIFO), same as the Unbilled
            tab. Empty once everything has been pulled onto the invoice. */}
        {clientId && (loadingWork || available.length > 0 || pulled.length > 0) && (
          <div className="fin-worklist">
            {loadingWork ? (
              <span className="fin-sub">Looking for unbilled work…</span>
            ) : available.length > 0 ? (
              <>
                <div className="fin-worklist-head">
                  <span className="fin-form-label">
                    Unbilled work for this client — {available.length} charge{available.length === 1 ? '' : 's'}, {money(availableTotal)}
                  </span>
                  <Button variant="secondary" size="sm" onClick={() => addCharges(available)}>
                    Add all ({money(availableTotal)})
                  </Button>
                </div>
                <ul className="fin-worklist-rows">
                  {available.map((c) => (
                    <li key={c.billItemId}>
                      <span className="fin-worklist-desc">{c.description}</span>
                      {c.taskNumber != null && (
                        <span className="fin-code">TASK-{String(c.taskNumber).padStart(4, '0')}</span>
                      )}
                      <span className="fin-worklist-kind">{c.kind === 'ope' ? 'Reimbursement' : 'Fees'}</span>
                      {c.ageDays != null && <span className="fin-worklist-age">waiting {ageLabel(c.ageDays)}</span>}
                      <span className="fin-worklist-amt">{money(c.amount)}</span>
                      <button type="button" className="fin-link fin-link-btn" onClick={() => addCharges([c])}>
                        Add
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <span className="fin-sub">
                All {pulled.length} of this client's unbilled charge{pulled.length === 1 ? '' : 's'} are on this
                invoice. Remove a line to put it back on offer.
              </span>
            )}
          </div>
        )}

        <div className="fin-lines">
          <span className="fin-form-label">Lines</span>
          {lines.map((l, i) => (
            <div className="fin-line" key={i}>
              <input
                className="ds-input fin-line-desc" placeholder="Description"
                value={l.description} onChange={(e) => setLine(i, 'description', e.target.value)}
              />
              <select className="ds-select fin-line-kind" value={l.kind} onChange={(e) => setLine(i, 'kind', e.target.value)}>
                {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
              <input
                className="ds-input fin-line-amt" type="number" min="0" step="0.01" placeholder="0.00"
                value={l.amount} onChange={(e) => setLine(i, 'amount', e.target.value)}
              />
              <button
                type="button" className="fin-line-del" aria-label="Remove line"
                onClick={() => removeLine(i)}
              >✕</button>
            </div>
          ))}
          {/* Which lines came from real work, so a hand-typed figure is visibly
              a hand-typed figure. */}
          {pulled.length > 0 && (
            <span className="fin-sub">
              {pulled.length} line{pulled.length === 1 ? '' : 's'} pulled from this client's tasks —
              those charges will be marked billed when the invoice is issued.
            </span>
          )}
          <Button variant="secondary" size="sm" onClick={() => setLines((p) => [...p, blankLine()])}>+ Add line</Button>
        </div>

        <label className="fin-toggle">
          <input type="checkbox" checked={gstApplicable} onChange={(e) => setGstApplicable(e.target.checked)} />
          <span>
            <b>Charge GST</b>
            <small>
              Applied to professional fees only. Reimbursements recovered as a pure agent stay outside
              the value of supply. The CGST/SGST vs IGST split is decided by the client's place of supply.
            </small>
          </span>
        </label>

        <div className="ds-field">
          <label className="ds-field-label" htmlFor="fin-notes">Notes on the invoice</label>
          <textarea id="fin-notes" className="ds-input" rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        <div className="fin-preview">
          <div><span>Professional fees</span><b>{money(preview.taxable)}</b></div>
          {preview.exempt > 0 && <div><span>Reimbursements (no GST)</span><b>{money(preview.exempt)}</b></div>}
          {gstApplicable && <div><span>GST @ {settings?.gstRate || 0}%</span><b>{money(preview.tax)}</b></div>}
          <div className="fin-preview-total"><span>Invoice total</span><b>{money(preview.total)}</b></div>
          {preview.tdsPct > 0 && (
            <>
              <div className="fin-preview-muted"><span>Less TDS @ {preview.tdsPct}% (deducted by the client)</span><b>−{money(preview.tds)}</b></div>
              <div className="fin-preview-muted"><span>Expected in the bank</span><b>{money(preview.expected)}</b></div>
            </>
          )}
        </div>
        <p className="fin-sub">
          Created as a draft — it takes an invoice number only when you issue it, so a discarded draft
          leaves no hole in the series.
        </p>
      </div>
    </Modal>
  );
}

export default InvoiceBuilder;
